import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, Copy, Gift, Share2, TrendingUp, Check } from 'lucide-react';
import { referralApi } from '../../api';
import { useTelegram } from '../../hooks';
import toast from 'react-hot-toast';

interface ReferralStats {
  totalReferrals: number;
  totalEarnings: number;
  referralCode: string;
  referralLink: string;
  tiers: {
    level1: { count: number; earnings: number; percentage: number };
    level2: { count: number; earnings: number; percentage: number };
    level3: { count: number; earnings: number; percentage: number };
  };
  recentReferrals: Array<{
    username: string;
    joinedAt: string;
    earnings: number;
  }>;
}

export function ReferralSystem() {
  const [stats, setStats] = useState<ReferralStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const { hapticFeedback, hapticNotification, shareUrl } = useTelegram();

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const data = await referralApi.getStats();
      
      // Convert tiers array to object format, or use level1/level2/level3 properties
      const tiersObj = {
        level1: { count: data.level1?.count || 0, earnings: 0, percentage: data.level1?.percentage || 10 },
        level2: { count: data.level2?.count || 0, earnings: 0, percentage: data.level2?.percentage || 5 },
        level3: { count: data.level3?.count || 0, earnings: 0, percentage: data.level3?.percentage || 2 },
      };
      
      // If tiers is an array, convert it to object format
      if (Array.isArray(data.tiers)) {
        data.tiers.forEach((tier) => {
          if (tier.level === 1) tiersObj.level1 = { count: tier.count, earnings: 0, percentage: tier.percentage || 10 };
          if (tier.level === 2) tiersObj.level2 = { count: tier.count, earnings: 0, percentage: tier.percentage || 5 };
          if (tier.level === 3) tiersObj.level3 = { count: tier.count, earnings: 0, percentage: tier.percentage || 2 };
        });
      }
      
      // Map API response to expected format
      setStats({
        totalReferrals: data.totalReferrals,
        totalEarnings: data.totalEarnings,
        referralCode: data.referralCode || data.code,
        referralLink: data.referralLink || `https://t.me/gemifaucet_bot/gemifaucet?startapp=${data.referralCode || data.code}`,
        tiers: tiersObj,
        recentReferrals: data.recentReferrals?.map(r => ({
          ...r,
          joinedAt: typeof r.joinedAt === 'string' ? r.joinedAt : new Date(r.joinedAt).toISOString(),
        })) || [],
      });
    } catch (error) {
      console.error('Failed to fetch referral stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyLink = async () => {
    if (!stats?.referralLink) return;

    try {
      await navigator.clipboard.writeText(stats.referralLink);
      setCopied(true);
      hapticNotification('success');
      toast.success('Link copied!');

      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  const shareLink = () => {
    if (!stats?.referralLink) return;
    
    hapticFeedback('medium');
    
    const text = `🎁 Join me on Gemifaucet and earn free crypto!\n\nUse my referral link to get started with bonus points:`;
    shareUrl(stats.referralLink, text);
  };

  if (loading) {
    return (
      <div className="card p-6">
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-6"
    >
      {/* Header card */}
      <div className="card p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
            <Users className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-lg">Referral Program</h3>
            <p className="text-white/60 text-sm">Invite friends & earn together</p>
          </div>
        </div>

        {/* Referral link */}
        <div className="mb-4">
          <label className="text-sm text-white/60 mb-2 block">Your Referral Link</label>
          <div className="flex gap-2">
            <div className="flex-1 bg-white/10 rounded-xl px-4 py-3 font-mono text-sm truncate">
              {stats?.referralLink || 'Loading...'}
            </div>
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={copyLink}
              className="px-4 py-3 rounded-xl bg-white/10 hover:bg-white/20 transition-colors"
            >
              {copied ? (
                <Check className="w-5 h-5 text-green-400" />
              ) : (
                <Copy className="w-5 h-5" />
              )}
            </motion.button>
          </div>
        </div>

        {/* Share buttons */}
        <div className="grid grid-cols-2 gap-3">
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={copyLink}
            className="flex items-center justify-center gap-2 py-3 rounded-xl bg-white/10 hover:bg-white/20 transition-colors"
          >
            <Copy className="w-4 h-4" />
            <span>Copy Link</span>
          </motion.button>
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={shareLink}
            className="flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-blue-500 to-purple-500"
          >
            <Share2 className="w-4 h-4" />
            <span>Share</span>
          </motion.button>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card p-4 text-center"
        >
          <Users className="w-6 h-6 mx-auto mb-2 text-blue-400" />
          <p className="text-white/60 text-xs mb-1">Total Referrals</p>
          <p className="font-bold text-2xl">{stats?.totalReferrals || 0}</p>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card p-4 text-center"
        >
          <Gift className="w-6 h-6 mx-auto mb-2 text-green-400" />
          <p className="text-white/60 text-xs mb-1">Total Earnings</p>
          <p className="font-bold text-2xl text-green-400">+{stats?.totalEarnings || 0}</p>
        </motion.div>
      </div>

      {/* Tier breakdown */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="card p-6"
      >
        <h4 className="font-bold mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-yellow-400" />
          Commission Tiers
        </h4>

        <div className="space-y-4">
          {/* Tier 1 */}
          <div className="flex items-center justify-between p-3 rounded-xl bg-white/5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center font-bold">
                1
              </div>
              <div>
                <p className="font-medium">Direct Referrals</p>
                <p className="text-white/50 text-sm">{stats?.tiers.level1.percentage || 10}% commission</p>
              </div>
            </div>
            <div className="text-right">
              <p className="font-bold">{stats?.tiers.level1.count || 0}</p>
              <p className="text-green-400 text-sm">+{stats?.tiers.level1.earnings || 0}</p>
            </div>
          </div>

          {/* Tier 2 */}
          <div className="flex items-center justify-between p-3 rounded-xl bg-white/5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center font-bold">
                2
              </div>
              <div>
                <p className="font-medium">2nd Level</p>
                <p className="text-white/50 text-sm">{stats?.tiers.level2.percentage || 5}% commission</p>
              </div>
            </div>
            <div className="text-right">
              <p className="font-bold">{stats?.tiers.level2.count || 0}</p>
              <p className="text-green-400 text-sm">+{stats?.tiers.level2.earnings || 0}</p>
            </div>
          </div>

          {/* Tier 3 */}
          <div className="flex items-center justify-between p-3 rounded-xl bg-white/5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center font-bold">
                3
              </div>
              <div>
                <p className="font-medium">3rd Level</p>
                <p className="text-white/50 text-sm">{stats?.tiers.level3.percentage || 2}% commission</p>
              </div>
            </div>
            <div className="text-right">
              <p className="font-bold">{stats?.tiers.level3.count || 0}</p>
              <p className="text-green-400 text-sm">+{stats?.tiers.level3.earnings || 0}</p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Recent referrals */}
      {stats?.recentReferrals && stats.recentReferrals.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="card p-6"
        >
          <h4 className="font-bold mb-4">Recent Referrals</h4>
          <div className="space-y-3">
            {stats.recentReferrals.map((ref, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 rounded-xl bg-white/5"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-primary-dark flex items-center justify-center text-sm font-bold">
                    {ref.username.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-medium">@{ref.username}</p>
                    <p className="text-white/40 text-xs">
                      {new Date(ref.joinedAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <span className="text-green-400 font-medium">+{ref.earnings}</span>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* How it works */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="card p-6"
      >
        <h4 className="font-bold mb-4">How It Works</h4>
        <div className="space-y-4">
          <div className="flex gap-3">
            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold flex-shrink-0">
              1
            </div>
            <div>
              <p className="font-medium">Share Your Link</p>
              <p className="text-white/50 text-sm">Send your unique referral link to friends</p>
            </div>
          </div>
          <div className="flex gap-3">
            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold flex-shrink-0">
              2
            </div>
            <div>
              <p className="font-medium">Friends Join</p>
              <p className="text-white/50 text-sm">They sign up using your link and start earning</p>
            </div>
          </div>
          <div className="flex gap-3">
            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold flex-shrink-0">
              3
            </div>
            <div>
              <p className="font-medium">Earn Forever</p>
              <p className="text-white/50 text-sm">Get commission on all their earnings, forever!</p>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
